// Shared types & hooks for the Executive Reasoning Engine. Every dashboard
// surface (Brief, Action Center, Assistant Explain dialog, notifications)
// reads from `useExecutiveSnapshot` so recommendations, priorities, and
// risk detection have exactly one source of truth.
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Subscribe once per hook consumer to source tables that feed the executive
 * reasoning engine so a state change (e.g. a vendor being marked reviewed)
 * refreshes the snapshot everywhere without a manual reload.
 */
function useExecSnapshotRealtime(qc: QueryClient) {
  useEffect(() => {
    const suffix = Math.random().toString(36).slice(2);
    const invalidate = () =>
      qc.invalidateQueries({ queryKey: EXEC_SNAPSHOT_KEY });
    const channel = supabase
      .channel(`exec-snapshot-${suffix}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "popup_vendor_registrations" },
        invalidate,
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "exec_recommendations" },
        invalidate,
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [qc]);
}

export type Priority = "critical" | "high" | "medium" | "low";

export interface ExecRecommendation {
  id: string;
  kind: string;
  rule_id: string;
  priority: Priority;
  score: number;
  title: string;
  summary: string | null;
  recommendation: string | null;
  reasoning: {
    signals: Record<string, unknown>;
    rule: string;
    weights: Record<string, number>;
    confidence: number;
  };
  linked_records: Array<{ table: string; id: string; label?: string; href?: string }>;
  status: "open" | "dismissed" | "deferred" | "completed" | "stale";
  dedupe_key: string;
  created_at: string;
  updated_at: string;
}

export interface ExecutiveSnapshot {
  generated_at: string;
  brief: {
    revenue: {
      today_cents: number;
      week_cents: number;
      month_cents: number;
      outstanding_cents: number;
    };
    counts: {
      urgent: number;
      high: number;
      waiting_others: number;
      pending_proposals: number;
      instructor_ready: number;
      instructor_blocked: number;
      workflows: number;
    };
    top_priorities: ExecRecommendation[];
  };
  recommendations: ExecRecommendation[];
}

export const EXEC_SNAPSHOT_KEY = ["executive-snapshot"] as const;

/**
 * Canonical fetcher for the Executive Snapshot. Both the UI ("Needs Your
 * Attention", Executive Brief, Action Center) and the Silver Sage Assistant's
 * `get_executive_snapshot` tool consume executive-reasoning's `snapshot`
 * action — this helper is the single client-side entrypoint so no consumer
 * can silently drift onto a parallel data source. See regression test at
 * src/lib/executive/__tests__/engine.shared-source.test.ts.
 */
export async function fetchExecutiveSnapshot(): Promise<ExecutiveSnapshot> {
  const { data, error } = await supabase.functions.invoke("executive-reasoning", {
    body: { action: "snapshot" },
  });
  if (error) throw error;
  const snap = data as ExecutiveSnapshot;

  // Operational Truth gate: never present a recommendation whose target
  // cannot be traced to authoritative data. We consult the enrollment board
  // + resolver-backed instructor set + known registration periods. Anything
  // that fails validation is dropped from the snapshot and logged as a
  // diagnostic so the Executive Office never fabricates operational reality.
  try {
    const [{ buildBoardStates }, { validateRecommendations }, { fetchEnrollmentData }, { buildEnrollmentBoard }] =
      await Promise.all([
        import("@/lib/operationalTruth"),
        import("@/lib/recommendationValidator"),
        import("@/lib/enrollment"),
        import("@/lib/enrollmentBoard"),
      ]);
    const { payments, interest, children } = await fetchEnrollmentData();
    const board = buildEnrollmentBoard(payments, interest, children, { includeClosed: true });
    const classStates = buildBoardStates(board.classes);

    // Instructors + registration periods — pulled from authoritative tables.
    const [{ data: instrRows }, { data: periodRows }] = await Promise.all([
      supabase.from("instructors").select("id").limit(2000),
      supabase.from("registration_periods").select("id").limit(500),
    ]);
    const knownInstructorIds = new Set<string>((instrRows ?? []).map((r: any) => String(r.id)));
    const knownRegistrationPeriodIds = new Set<string>(
      (periodRows ?? []).map((r: any) => String(r.id)),
    );

    const validated = validateRecommendations(snap.recommendations ?? [], {
      classStates,
      knownInstructorIds,
      knownRegistrationPeriodIds,
    });

    if (validated.suppressed.length > 0) {
      console.warn("[MAVIS↔UI] suppressed unverifiable recommendations", validated.suppressed);
    }

    // Reattach confidence as a non-breaking field on each recommendation so
    // downstream UI can honor it without changing wording.
    const enriched = validated.valid.map((v) => ({
      ...v.recommendation,
      confidence: v.confidence,
      reason_chain: v.reasonChain,
    })) as unknown as ExecRecommendation[];

    snap.recommendations = enriched as any;
    snap.brief.top_priorities = enriched.filter((r) =>
      (snap.brief.top_priorities ?? []).some((t) => t.id === r.id),
    ) as any;
  } catch (e) {
    console.warn("[MAVIS↔UI] validator gate skipped due to error", e);
  }

  if (typeof console !== "undefined") {
    const ids = (snap.recommendations ?? []).map((r) => r.id);
    console.info("[MAVIS↔UI] executive snapshot fetched", {
      count: ids.length,
      ids,
      generated_at: snap.generated_at,
    });
  }
  return snap;
}

export function useExecutiveSnapshot() {
  const qc = useQueryClient();
  useExecSnapshotRealtime(qc);
  return useQuery<ExecutiveSnapshot>({
    queryKey: EXEC_SNAPSHOT_KEY,
    queryFn: fetchExecutiveSnapshot,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}


export function useInvalidateSnapshot() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: EXEC_SNAPSHOT_KEY });
}

export async function explainRecommendation(recommendationId: string) {
  const { data, error } = await supabase.functions.invoke("executive-reasoning", {
    body: { action: "explain", recommendation_id: recommendationId },
  });
  if (error) throw error;
  return data as { recommendation: ExecRecommendation; records: Array<{ table: string; id: string; data: any }> };
}

export async function recordRecommendationResponse(
  recommendationId: string,
  response: "dismiss" | "complete" | "defer" | "waiting",
  note?: string,
  deferUntil?: string,
) {
  if (!recommendationId) {
    throw new Error("Recommendation is missing an id — refresh the dashboard and try again.");
  }
  const { data, error } = await supabase.functions.invoke("executive-reasoning", {
    body: {
      action: "record_response",
      recommendation_id: recommendationId,
      response,
      note,
      defer_until: deferUntil,
    },
  });
  if (error) throw error;
  return data;
}

export interface MorningResetPreview {
  counts: {
    total_open: number;
    will_archive: number;
    will_keep: number;
    by_category: Record<string, number>;
  };
  will_archive: ExecRecommendation[];
  will_archive_grouped: Record<string, ExecRecommendation[]>;
  will_keep: ExecRecommendation[];
}

export async function fetchMorningResetPreview(): Promise<MorningResetPreview> {
  const { data, error } = await supabase.functions.invoke("executive-reasoning", {
    body: { action: "morning_reset_preview" },
  });
  if (error) throw error;
  console.info("Morning Reset deployment diagnostics", {
    action: "morning_reset_preview",
    function_version: (data as any)?.function_version,
    deployed_at: (data as any)?.deployed_at,
    request_id: (data as any)?.request_id,
  });
  return data as MorningResetPreview;
}

export async function executeMorningReset(
  ids?: string[],
  opts: { mode?: 'smart' | 'clear_all' } = {},
) {
  const { data, error } = await supabase.functions.invoke("executive-reasoning", {
    body: {
      action: "morning_reset_execute",
      ids,
      mode: opts.mode ?? 'smart',
      source: opts.mode === 'clear_all' ? "ui:clear_dashboard" : "ui:morning_reset",
    },
  });
  if (error) throw error;
  console.info("Morning Reset deployment diagnostics", {
    action: "morning_reset_execute",
    mode: opts.mode ?? 'smart',
    function_version: (data as any)?.function_version,
    deployed_at: (data as any)?.deployed_at,
    request_id: (data as any)?.request_id,
    archived: (data as any)?.archived,
    failed: (data as any)?.failed,
    message: (data as any)?.message,
  });
  return data as { ok: boolean; archived: number; batch_id: string | null; counts: Record<string, number> };
}


export async function listArchivedRecommendations(opts: {
  limit?: number;
  offset?: number;
  category?: string;
  search?: string;
} = {}) {
  const { data, error } = await supabase.functions.invoke("executive-reasoning", {
    body: { action: "list_archived", ...opts },
  });
  if (error) throw error;
  return data as { items: (ExecRecommendation & { archive_category: string; archive_reason: string; archived_at: string })[]; total: number };
}

export async function restoreRecommendation(recommendationId: string) {
  const { data, error } = await supabase.functions.invoke("executive-reasoning", {
    body: { action: "restore_recommendation", recommendation_id: recommendationId },
  });
  if (error) throw error;
  return data;
}


export const PRIORITY_ORDER: Record<Priority, number> = { critical: 0, high: 1, medium: 2, low: 3 };

export function priorityBadgeClass(p: Priority): string {
  switch (p) {
    case "critical":
      return "bg-destructive text-destructive-foreground";
    case "high":
      return "bg-orange-500 text-white";
    case "medium":
      return "bg-amber-500 text-white";
    default:
      return "bg-muted text-muted-foreground";
  }
}

export interface RecommendationView {
  id: string;
  priority: "critical" | "high" | "normal" | "low";
  title: string;
  detail: string;
  href: string;
  createdAt: string;
}

/**
 * Shared projection used by "Needs Your Attention" so the panel and MAVIS
 * agree row-for-row. Priority is normalized to the UI's four-band scale.
 */
export function recommendationsToAttentionItems(
  recs: ExecRecommendation[] | undefined,
): RecommendationView[] {
  if (!recs?.length) return [];
  return recs
    .filter((r) => r.status === "open")
    .slice()
    .sort((a, b) => (PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority]) || (b.score - a.score))
    .map((r) => ({
      id: r.id,
      priority: r.priority === "medium" ? "normal" : r.priority,
      title: r.title,
      detail: r.summary ?? r.recommendation ?? "",
      href: r.linked_records?.[0]?.href ?? "/admin/action-center",
      createdAt: r.created_at,
    }));
}

